/**
 * BaseModal Component
 * Reusable modal wrapper with consistent styling and behavior
 *
 * Features:
 * - Consistent backdrop and container styling
 * - Click-outside-to-close functionality
 * - Escape key support via useEscapeKey hook
 * - Configurable sizes (small, medium, large)
 * - Proper event handling to prevent backdrop clicks from closing when clicking inside
 */

import { type ReactNode, useRef } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { MODAL_BACKDROP_STYLE, MODAL_CONTAINER_STYLE, MODAL_TITLE_STYLE } from '../../styles/modalStyles';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'small' | 'medium' | 'large';
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  loading?: boolean;
}

const SIZE_MAP = {
  small: '400px',
  medium: '600px',
  large: '800px',
} as const;

export function BaseModal({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  closeOnBackdropClick = false,
  closeOnEscape = true,
  loading = false,
}: BaseModalProps) {
  // Close modal on Escape key
  useEscapeKey({
    enabled: isOpen && closeOnEscape && !loading,
    onEscape: onClose
  });

  // Track whether the drag started inside the modal content.
  // If mousedown originated inside, releasing outside should not close the modal.
  const dragStartedInsideRef = useRef(false);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (dragStartedInsideRef.current) {
      dragStartedInsideRef.current = false;
      return;
    }
    if (closeOnBackdropClick && !loading) {
      onClose();
    }
  };

  const handleContentMouseDown = () => {
    dragStartedInsideRef.current = true;
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div style={MODAL_BACKDROP_STYLE} onClick={handleBackdropClick}>
      <div
        style={{
          ...MODAL_CONTAINER_STYLE,
          maxWidth: SIZE_MAP[size],
        }}
        onMouseDown={handleContentMouseDown}
        onClick={handleContentClick}
      >
        <h2 style={MODAL_TITLE_STYLE}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
